import { jsPDF } from 'jspdf'
import logoBahiol from '@/assets/logo.png'

/**
 * Genera la carta de una empresa como PDF.
 *
 * SEGUNDA ITERACION - version corta. La cabecera usa las TRES primeras
 * imagenes de la empresa, repartidas de lado a lado. Si tiene menos, se
 * reparten las que haya; si no tiene ninguna, cae al degradado azul.
 *
 * El texto se dibuja con las fuentes del PDF y no como imagen, asi que se
 * puede seleccionar y copiar, sale nitido a cualquier zoom y el archivo pesa
 * unas pocas decenas de KB en vez de cientos.
 */

/* A4 en puntos, que es la unidad nativa del PDF */
const ANCHO = 595.28
const ALTO = 841.89
const MARGEN = 44

const AZUL = [25, 118, 210]
const AZUL_OSCURO = [13, 63, 117]
const TINTA = [26, 26, 26]
const GRIS = [102, 112, 133]
const GRIS_CLARO = [152, 162, 179]
const LINEA = [236, 239, 241]

/* La cabecera son dos bandas: arriba las fotos limpias, debajo la barra
   azul con el nombre. Antes iban superpuestas y la capa que hacia legible el
   texto dejaba las fotos deslavadas. */
const ALTO_FOTOS = 104
const ALTO_TITULO = 92
const ALTO_CABECERA = ALTO_FOTOS + ALTO_TITULO
/* Cada producto es una tarjeta de ancho completo: fila a fila, pero con su
   propio marco redondeado en vez de un simple filete separador. */
const ALTO_TARJETA = 56
const HUECO_FILAS = 10
const ALTO_FILA = ALTO_TARJETA + HUECO_FILAS
const LADO_MINIATURA = 42
const RELLENO = 7
const ALTO_PIE = 46

function cargarImagen(ruta) {
  return new Promise((resolver, rechazar) => {
    const img = new Image()
    img.onload = () => resolver(img)
    img.onerror = () => rechazar(new Error('No se pudo cargar el logo'))
    img.src = ruta
  })
}

/**
 * Reduce el logo al tamano en que se va a ver.
 *
 * logo.png son 1088x960 y pesa 589 KB, pero en la carta se dibuja a 34 puntos
 * de alto. Metido tal cual, jsPDF lo incrusta SIN COMPRIMIR y una carta de una
 * sola hoja acababa pesando 4 MB. Reducido a la altura real por 3 (para que
 * aguante la impresion) baja a unos pocos KB.
 */
function logoReducido(logo, altoDestino) {
  const alto = Math.round(altoDestino * 3)
  const ancho = Math.round((logo.width * alto) / logo.height)

  const lienzo = document.createElement('canvas')
  lienzo.width = ancho
  lienzo.height = alto
  lienzo.getContext('2d').drawImage(logo, 0, 0, ancho, alto)

  return lienzo.toDataURL('image/png')
}

/**
 * Recorta la foto de un producto a un cuadrado, tipo "cover".
 *
 * jsPDF no sabe recortar: si se le pasa una foto apaisada la deforma para
 * meterla en el cuadrado. Se resuelve antes en un canvas, que de paso baja la
 * resolucion al tamano real de la miniatura y el PDF no se dispara.
 */
function recorteCuadrado(foto) {
  const lado = Math.round(LADO_MINIATURA * 3)
  const radio = Math.round(lado * 0.14)

  const lienzo = document.createElement('canvas')
  lienzo.width = lado
  lienzo.height = lado

  const ctx = lienzo.getContext('2d')

  // Blanco primero: las esquinas redondeadas se recortan aqui y lo que queda
  // fuera se ve blanco, que es el color de la hoja. Asi se puede seguir
  // guardando en JPEG, que pesa mucho menos que un PNG con transparencia.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, lado, lado)

  ctx.save()
  esquinasRedondeadas(ctx, 0, 0, lado, lado, radio)
  ctx.clip()

  const proporcion = Math.max(lado / foto.width, lado / foto.height)
  const ancho = foto.width * proporcion
  const alto = foto.height * proporcion
  ctx.drawImage(foto, (lado - ancho) / 2, (lado - alto) / 2, ancho, alto)
  ctx.restore()

  return lienzo.toDataURL('image/jpeg', 0.82)
}

/** Trazo de un rectangulo con esquinas redondeadas sobre un canvas */
function esquinasRedondeadas(ctx, x, y, ancho, alto, radio) {
  ctx.beginPath()
  ctx.moveTo(x + radio, y)
  ctx.lineTo(x + ancho - radio, y)
  ctx.quadraticCurveTo(x + ancho, y, x + ancho, y + radio)
  ctx.lineTo(x + ancho, y + alto - radio)
  ctx.quadraticCurveTo(x + ancho, y + alto, x + ancho - radio, y + alto)
  ctx.lineTo(x + radio, y + alto)
  ctx.quadraticCurveTo(x, y + alto, x, y + alto - radio)
  ctx.lineTo(x, y + radio)
  ctx.quadraticCurveTo(x, y, x + radio, y)
  ctx.closePath()
}

function precioFormateado(costs) {
  const valor = Number(costs)

  if (costs === null || costs === undefined || costs === '' || isNaN(valor)) {
    return 'Consultar'
  }
  return `$ ${valor.toLocaleString('es-CO')}`
}

/** Corta el texto con puntos suspensivos si no cabe en el ancho dado */
function recortar(pdf, texto, anchoMaximo) {
  if (!texto) {
    return ''
  }
  if (pdf.getTextWidth(texto) <= anchoMaximo) {
    return texto
  }

  let corte = texto
  while (corte.length > 1 && pdf.getTextWidth(corte + '...') > anchoMaximo) {
    corte = corte.slice(0, -1)
  }
  return corte.trimEnd() + '...'
}

/**
 * Arma la franja de la cabecera y la devuelve como imagen para incrustarla.
 *
 * Con foto de la empresa: la foto recortada al ancho de la hoja mas una capa
 * oscura encima. La capa no es decoracion, es lo que permite que el nombre en
 * blanco se lea sobre una foto cualquiera, que puede ser clara.
 *
 * Sin foto: el degradado azul de siempre.
 *
 * Se arma en canvas porque jsPDF no sabe recortar imagenes ni superponer
 * capas: solo coloca lo que se le da, ya resuelto.
 */
function franjaCabecera(fotos) {
  const escala = 2 // el doble de resolucion para que no se vea pixelado al imprimir
  const ancho = Math.round(ANCHO * escala)
  const alto = Math.round(ALTO_FOTOS * escala)

  const lienzo = document.createElement('canvas')
  lienzo.width = ancho
  lienzo.height = alto
  const ctx = lienzo.getContext('2d')

  if (fotos.length > 0) {
    // Se reparten a lo ancho: con tres fotos son tres tercios, con una ocupa
    // toda la franja. Cada una va recortada tipo "cover" en su hueco, sin
    // deformarse.
    const anchoHueco = ancho / fotos.length

    fotos.forEach((foto, indice) => {
      const x = anchoHueco * indice
      const proporcion = Math.max(anchoHueco / foto.width, alto / foto.height)
      const anchoFoto = foto.width * proporcion
      const altoFoto = foto.height * proporcion

      ctx.save()
      ctx.beginPath()
      ctx.rect(x, 0, anchoHueco, alto)
      ctx.clip()
      ctx.drawImage(
        foto,
        x + (anchoHueco - anchoFoto) / 2,
        (alto - altoFoto) / 2,
        anchoFoto,
        altoFoto
      )
      ctx.restore()
    })

    // Separador fino entre foto y foto
    if (fotos.length > 1) {
      ctx.fillStyle = '#ffffff'
      for (let i = 1; i < fotos.length; i++) {
        ctx.fillRect(anchoHueco * i - 1.5, 0, 3, alto)
      }
    }
  } else {
    const grad = ctx.createLinearGradient(0, 0, ancho, alto)
    grad.addColorStop(0, '#42a5f5')
    grad.addColorStop(0.45, '#1976d2')
    grad.addColorStop(1, '#0d3f75')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, ancho, alto)
  }

  return lienzo.toDataURL('image/jpeg', 0.88)
}

function dibujarCabecera(pdf, empresa, franja) {
  // Banda de fotos, sin nada encima
  pdf.addImage(franja, 'JPEG', 0, 0, ANCHO, ALTO_FOTOS)

  // Titulo sobre blanco y centrado, como una carta de restaurante. Antes era
  // una barra azul oscura con el texto a la izquierda: pesaba demasiado y
  // parecia el encabezado de un folleto, no de una carta.
  const centro = ANCHO / 2

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(24)
  pdf.setTextColor(...TINTA)
  pdf.text(
    recortar(pdf, empresa?.company || 'Carta', ANCHO - MARGEN * 2),
    centro,
    ALTO_FOTOS + 40,
    { align: 'center' }
  )

  const ubicacion = [empresa?.city, empresa?.country].filter(Boolean).join(', ')
  if (ubicacion) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    pdf.setTextColor(...GRIS)
    pdf.text(ubicacion, centro, ALTO_FOTOS + 57, { align: 'center' })
  }

  // Filete corto y centrado en vez de una linea de lado a lado
  pdf.setDrawColor(...AZUL)
  pdf.setLineWidth(1.2)
  pdf.line(centro - 26, ALTO_FOTOS + 72, centro + 26, ALTO_FOTOS + 72)
}

function dibujarPie(pdf, empresa, pagina, totalPaginas, logo, anchoLogo, altoLogo) {
  pdf.setFillColor(242, 245, 249)
  pdf.rect(0, ALTO - ALTO_PIE, ANCHO, ALTO_PIE, 'F')

  // El logo vive aqui, discreto: en la cabecera competia con el nombre de la
  // empresa, que es lo que tiene que destacar en una carta
  pdf.addImage(logo, 'PNG', MARGEN, ALTO - 36, anchoLogo, altoLogo, 'logoBahiol')

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(...GRIS)
  pdf.text('Ver la carta completa en Bahiol', MARGEN + anchoLogo + 10, ALTO - 24)

  if (empresa?.idCompany) {
    const url = `${window.location.origin}/viewCompany/${empresa.idCompany}`
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(...AZUL)
    pdf.text(url, ANCHO - MARGEN, ALTO - 26, { align: 'right' })
    // Enlace real: en el PDF se puede hacer clic
    pdf.link(ANCHO - MARGEN - pdf.getTextWidth(url), ALTO - 34, pdf.getTextWidth(url), 12, { url })
  }

  if (totalPaginas > 1) {
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(...GRIS_CLARO)
    pdf.setFontSize(8)
    pdf.text(`${pagina} de ${totalPaginas}`, ANCHO / 2, ALTO - 12, { align: 'center' })
  }
}

export async function basicoCartaPdf(empresa, imagenesEmpresa, productos) {
  // compress: sin esto jsPDF guarda los flujos en crudo y el archivo se
  // dispara de tamano
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true })

  const original = await cargarImagen(logoBahiol)
  const altoLogo = 22
  const anchoLogo = (original.width * altoLogo) / original.height
  const logo = logoReducido(original, altoLogo)

  // Las tres primeras. Si alguna no carga se sigue sin ella: una carta sin
  // fondo es mejor que ninguna carta.
  const primeras = (Array.isArray(imagenesEmpresa) ? imagenesEmpresa : []).slice(0, 3)
  const fotos = (
    await Promise.all(primeras.map(i => cargarImagen(i.pathBase64).catch(() => null)))
  ).filter(Boolean)
  const franja = franjaCabecera(fotos)

  const lista = Array.isArray(productos) ? productos : []

  // Miniatura de cada producto. La imagen ya viene en el objeto (la carga la
  // vista, para que este util siga sin saber de APIs). Si falta o no carga, la
  // fila sale con un recuadro gris en vez de romperse.
  const miniaturas = await Promise.all(
    lista.map(pr => (pr.imagen ? cargarImagen(pr.imagen).catch(() => null) : null))
  )
  const anchoPrecio = 90
  const anchoTexto =
    ANCHO - MARGEN * 2 - RELLENO * 2 - LADO_MINIATURA - 12 - anchoPrecio - 10
  const yInicio = ALTO_CABECERA + 44
  const yTope = ALTO - ALTO_PIE - 16

  // Cuantas filas caben por pagina, para saber el total antes de empezar y
  // poder numerar las paginas
  const porPagina = Math.max(1, Math.floor((yTope - yInicio) / ALTO_FILA))
  const totalPaginas = Math.max(1, Math.ceil(lista.length / porPagina))

  let pagina = 1
  let y = yInicio

  dibujarCabecera(pdf, empresa, franja)

  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.setTextColor(...AZUL)
  pdf.text('CARTA', MARGEN, ALTO_CABECERA + 22)

  if (lista.length === 0) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(12)
    pdf.setTextColor(...GRIS_CLARO)
    pdf.text('Esta empresa todavia no tiene productos publicados.', MARGEN, y)
  }

  lista.forEach((producto, indice) => {
    // Salto de pagina: ahora que es PDF de verdad no hay que recortar la lista
    if (indice > 0 && indice % porPagina === 0) {
      dibujarPie(pdf, empresa, pagina, totalPaginas, logo, anchoLogo, altoLogo)
      pdf.addPage()
      pagina++
      y = yInicio
      dibujarCabecera(pdf, empresa, franja)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10)
      pdf.setTextColor(...AZUL)
      pdf.text('CARTA', MARGEN, ALTO_CABECERA + 22)
    }

    const miniatura = miniaturas[indice]

    // Marco de la tarjeta
    pdf.setFillColor(255, 255, 255)
    pdf.setDrawColor(...LINEA)
    pdf.setLineWidth(0.8)
    pdf.roundedRect(MARGEN, y, ANCHO - MARGEN * 2, ALTO_TARJETA, 7, 7, 'FD')

    const xFoto = MARGEN + RELLENO
    const yFoto = y + RELLENO

    if (miniatura) {
      pdf.addImage(
        recorteCuadrado(miniatura),
        'JPEG',
        xFoto,
        yFoto,
        LADO_MINIATURA,
        LADO_MINIATURA
      )
    } else {
      pdf.setFillColor(240, 243, 246)
      pdf.roundedRect(xFoto, yFoto, LADO_MINIATURA, LADO_MINIATURA, 6, 6, 'F')
    }

    const xTexto = xFoto + LADO_MINIATURA + 12

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(12.5)
    pdf.setTextColor(...TINTA)
    pdf.text(recortar(pdf, producto.product, anchoTexto), xTexto, y + 24)

    if (producto.description) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(9.5)
      pdf.setTextColor(...GRIS)
      pdf.text(recortar(pdf, producto.description, anchoTexto), xTexto, y + 39)
    }

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(13)
    pdf.setTextColor(...AZUL_OSCURO)
    pdf.text(precioFormateado(producto.costs), ANCHO - MARGEN - RELLENO - 5, y + 32, {
      align: 'right'
    })

    y += ALTO_FILA
  })

  dibujarPie(pdf, empresa, pagina, totalPaginas, logo, anchoLogo, altoLogo)

  return pdf
}

function nombreDeArchivo(texto) {
  const limpio = (texto || 'bahiol')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

  return `carta-${limpio || 'bahiol'}.pdf`
}

export async function descargarBasicoCartaPdf(empresa, imagenesEmpresa, productos) {
  const pdf = await basicoCartaPdf(empresa, imagenesEmpresa, productos)
  pdf.save(nombreDeArchivo(empresa?.company))
}

export default { basicoCartaPdf, descargarBasicoCartaPdf }
